import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, TransparentCard } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { User, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import PageWithNavbar from "@/components/PageWithNavbar";
import { authAPI } from "../services/api";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan || null;
  const redirectState = location.state ? { ...location.state } : undefined;
  const { login, isAuthenticated, isLoading, error: authError } = useAuth();

  // If user is already authenticated, redirect to home
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const [formData, setFormData] = useState({
    identifier: "", // username or email
    password: "",
    rememberMe: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resend Verification State
  const [isResendVerification, setIsResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [isResending, setIsResending] = useState(false);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendStatus(null);
    if (!resendEmail) {
        setResendStatus({ type: 'error', message: "Email is required" });
        return;
    }
    
    setIsResending(true);
    try {
        await authAPI.resendVerification({ Email: resendEmail });
        setResendStatus({ type: 'success', message: "Verification email request sent." });
    } catch (err) {
        setResendStatus({ type: 'error', message: "Failed to send verification email. Please try again." });
    } finally {
        setIsResending(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.identifier.trim()) {
      newErrors.identifier = "Username or email is required";
    } else if (!/^[\w.@+-]+$/.test(formData.identifier)) {
      newErrors.identifier = "Please enter a valid username or email address";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    const response = await login(formData.identifier, formData.password, redirectState);
    console.log('[Login] response:', response);
    if(response)
    {
        console.log('[Login] going to checkout');
        if (selectedPlan && selectedPlan.id) 
        {
          goToCheckout();
        }
        else 
        {
          // If no selected plan, just redirect to home
          navigate("/");
        }
    }
    // Post-login redirect is handled by usePostLoginRedirect
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  // When user clicks "Sign up" or "Create one here", preserve payment/plan state if present
  const goToRegister = () => {
    if (location.state) {
      navigate("/register", { state: { ...location.state } });
    } else {
      navigate("/register");
    }
  };

  const goToCheckout = () => {
    console.log('[Login] Selected plan:', selectedPlan);
    if (selectedPlan) {
      navigate("/checkout", { state: { selectedPlan } });
    } else {
      navigate("/checkout");
    }
  };

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <h1 className="text-3xl font-bold">Welcome Back</h1>
            </div>
            <p className="text-muted-foreground">
              Sign in to access your Doyen Trading account
            </p>
          </div>

          {/* Selected Plan Display */}
          {selectedPlan && (
            <Card className="mb-6 bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold flex items-center">
                      <CheckCircle className="h-4 w-4 text-primary mr-2" />
                      Selected Plan: {selectedPlan.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedPlan.title === "Free" ? "Free forever" : `$${selectedPlan.price} one-time`}
                    </p>
                  </div>
                  <Badge variant={selectedPlan.title === "Premium" ? "default" : "secondary"}>
                    {selectedPlan.title}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <TransparentCard>
            <CardHeader>
              <CardTitle>{isResendVerification ? "Resend Verification" : "Sign In"}</CardTitle>
              <CardDescription>
                {isResendVerification 
                  ? "Enter your email to receive a new verification link" 
                  : "Enter your credentials to access your account"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isResendVerification ? (
                <form onSubmit={handleResendVerification} className="space-y-4">
                  {resendStatus && (
                    <div className={`flex items-center gap-2 p-3 text-sm border rounded-md ${
                      resendStatus.type === 'success' ? 'text-green-800 bg-green-100 border-green-300' : 'text-red-800 bg-red-100 border-red-300'
                    }`}>
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{resendStatus.message}</span>
                    </div>
                  )}

                  <div>
                    <label htmlFor="resendEmail" className="block text-sm font-medium mb-2">
                       Email Address
                    </label>
                    <div className="relative">
                      <Input
                        id="resendEmail"
                        type="email"
                        placeholder="Enter your email address"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="pl-10"
                      />
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    size="lg"
                    disabled={isResending}
                  >
                    {isResending ? "Sending..." : "Resend Verification Email"}
                  </Button>

                  <div className="text-center pt-4">
                    <button
                      type="button"
                      onClick={() => setIsResendVerification(false)}
                      className="text-primary hover:underline font-medium text-sm"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Show general user-friendly error if present */}
                {errors.general && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{errors.general}</span>
                  </div>
                )}
                {authError && authError !== "Password is required" && authError !== "Invalid password" && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{authError}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="identifier" className="block text-sm font-medium mb-2">
                    Username or Email
                  </label>
                  <div className="relative">
                    <Input
                      id="identifier"
                      type="text"
                      placeholder="Enter your username or email"
                      value={formData.identifier}
                      onChange={(e) => handleInputChange("identifier", e.target.value)}
                      className={`pl-10 ${errors.identifier ? "border-red-500" : ""}`}
                    />
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  </div>
                  {errors.identifier && (
                    <p className="text-red-500 text-xs mt-1">{errors.identifier}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className={`pl-10 pr-10 ${errors.password ? "border-red-500" : ""}`}
                    />
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={formData.rememberMe}
                      onChange={(e) => handleInputChange("rememberMe", e.target.checked)}
                      className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                      Remember me
                    </label>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>

                <div className="text-center pt-4 flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={goToRegister}
                      className="text-primary hover:underline font-medium"
                    >
                      Create one here
                    </button>
                  </p>
                  {authError === "Your account is pending email verification." && (
                  <p className="text-sm text-muted-foreground">
                    Link expired?{" "}
                    <button
                      type="button"
                      onClick={() => {
                         setIsResendVerification(true);
                         setResendStatus(null);
                         setResendEmail("");
                      }}
                      className="text-primary hover:underline font-medium"
                    >
                      Resend verification
                    </button>
                  </p>
                  )}
                </div>
              </form>
              )}
            </CardContent>
          </TransparentCard>
        </div>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default LoginPage;
